/**
 * Free tool widget: engagement-rate calculator. Pure client-side.
 * ER = (avg interactions per post / followers) * 100.
 */
import { useMemo, useState } from 'react';

function num(v: string): number {
  const n = Number(v.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function verdict(er: number): { label: string; color: string } {
  if (er >= 6) return { label: 'Excelente', color: 'text-green-600' };
  if (er >= 3) return { label: 'Muy bueno', color: 'text-green-600' };
  if (er >= 1) return { label: 'En la media', color: 'text-amber-600' };
  return { label: 'Por debajo de la media', color: 'text-red-600' };
}

export function EngagementCalculator() {
  const [followers, setFollowers] = useState('');
  const [likes, setLikes] = useState('');
  const [comments, setComments] = useState('');
  const [saves, setSaves] = useState('');

  const f = num(followers);
  const interactions = num(likes) + num(comments) + num(saves);
  const er = useMemo(() => (f > 0 ? (interactions / f) * 100 : 0), [f, interactions]);
  const show = f > 0 && interactions > 0;
  const v = verdict(er);

  const field = (label: string, value: string, set: (v: string) => void, hint?: string) => (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {hint && <span className="ml-1 text-xs text-slate-400">{hint}</span>}
      <input
        inputMode="numeric"
        value={value}
        onChange={(e) => set(e.target.value)}
        placeholder="0"
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 tabular-nums focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none"
      />
    </label>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
      <div className="space-y-4">
        {field('Seguidores', followers, setFollowers)}
        {field('Likes (media por post)', likes, setLikes)}
        {field('Comentarios (media por post)', comments, setComments)}
        {field('Guardados / compartidos', saves, setSaves, '(opcional)')}
      </div>

      <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
        <span className="text-sm text-slate-500">Tu tasa de engagement</span>
        <span className="mt-1 text-5xl font-bold tabular-nums text-slate-900">
          {show ? `${er.toFixed(2)}%` : '—'}
        </span>
        {show && <span className={`mt-2 text-sm font-semibold ${v.color}`}>{v.label}</span>}
        {!show && <span className="mt-2 text-xs text-slate-400">Introduce seguidores e interacción</span>}
      </div>
    </div>
  );
}
